import { getFixingSpec } from "@/config/designer";
import type { Fixing } from "@/core/fixings/types";
import { buildHoleProfile, type HoleProfile } from "@/core/fixings/holeSolid";
import { VoxelField } from "./VoxelField";

export function cloneField(src: VoxelField): VoxelField {
  const copy = new VoxelField(src.width, src.height, src.depth, src.h, src.pad);
  copy.data.set(src.data);
  copy.dirty.set(src.dirty);
  return copy;
}

/**
 * Resample the sculpted field onto a different voxel size. Used by the export
 * quality settings: a finer grid smooths the isosurface without inventing
 * detail, a coarser one keeps draft exports quick.
 */
export function resampleField(src: VoxelField, voxelSize: number): VoxelField {
  if (Math.abs(voxelSize - src.h) < 1e-9) return cloneField(src);
  const out = new VoxelField(src.width, src.height, src.depth, voxelSize, src.pad);

  for (let k = 0; k < out.nz; k++) {
    const z = out.sampleZ(k);
    for (let j = 0; j < out.ny; j++) {
      const y = out.sampleY(j);
      for (let i = 0; i < out.nx; i++) {
        const x = out.sampleX(i);
        const p = out.index(i, j, k);
        out.data[p] = out.clampValue(src.sample(x, y, z));
        out.dirty[p] = anyDirtyNear(src, x, y, z) ? 1 : 0;
      }
    }
  }
  return out;
}

/**
 * Conservative dirty test: if any source sample within a voxel has been
 * sculpted, the resampled sample counts as sculpted too. Being conservative
 * matters — the mesher only restores sharp blank edges where it is certain the
 * region is untouched.
 */
function anyDirtyNear(src: VoxelField, x: number, y: number, z: number): boolean {
  const i = Math.floor((x - src.ox) / src.h);
  const j = Math.floor((y - src.oy) / src.h);
  const k = Math.floor((z - src.oz) / src.h);
  for (let dk = 0; dk <= 1; dk++) {
    for (let dj = 0; dj <= 1; dj++) {
      for (let di = 0; di <= 1; di++) {
        const ii = i + di;
        const jj = j + dj;
        const kk = k + dk;
        if (ii < 0 || jj < 0 || kk < 0 || ii >= src.nx || jj >= src.ny || kk >= src.nz) continue;
        if ((src.dirty[src.index(ii, jj, kk)] as number) !== 0) return true;
      }
    }
  }
  return false;
}

/**
 * Fallback hole cutting: rasterise the fixing solids straight into the field.
 * Only used when the CSG engine is unavailable. The hole is real and passes
 * through to the rear face, but its walls are limited by the voxel size.
 */
export function rasterizeFixings(field: VoxelField, fixings: readonly Fixing[]): void {
  for (const fixing of fixings) {
    const spec = getFixingSpec(fixing.typeId);
    const profile = buildHoleProfile(field, spec, fixing.x, fixing.z);
    if (!profile) continue;

    const maxR = Math.max(profile.shaftRadius, profile.countersinkRadius, profile.blendRadius);
    const rear = pointAt(profile, profile.rearT);
    const top = pointAt(profile, profile.topT);
    const i0 = Math.max(0, Math.floor((Math.min(rear[0], top[0]) - maxR - field.ox) / field.h) - 1);
    const i1 = Math.min(field.nx - 1, Math.ceil((Math.max(rear[0], top[0]) + maxR - field.ox) / field.h) + 1);
    const j0 = Math.max(0, Math.floor((Math.min(rear[1], top[1]) - maxR - field.oy) / field.h) - 1);
    const j1 = Math.min(field.ny - 1, Math.ceil((Math.max(rear[1], top[1]) + maxR - field.oy) / field.h) + 1);
    const k0 = Math.max(0, Math.floor((Math.min(rear[2], top[2]) - maxR - field.oz) / field.h) - 1);
    const k1 = Math.min(field.nz - 1, Math.ceil((Math.max(rear[2], top[2]) + maxR - field.oz) / field.h) + 1);

    for (let k = k0; k <= k1; k++) {
      const z = field.sampleZ(k);
      for (let j = j0; j <= j1; j++) {
        const y = field.sampleY(j);
        for (let i = i0; i <= i1; i++) {
          const x = field.sampleX(i);
          const rx = x - profile.surfacePoint[0];
          const ry = y - profile.surfacePoint[1];
          const rz = z - profile.surfacePoint[2];
          const t = rx * profile.axis[0] + ry * profile.axis[1] + rz * profile.axis[2];
          if (t < profile.rearT || t > profile.topT) continue;
          const px = rx - profile.axis[0] * t;
          const py = ry - profile.axis[1] * t;
          const pz = rz - profile.axis[2] * t;
          const radial = Math.hypot(px, py, pz);
          const radialInside = radiusAt(profile, t) - radial;
          const value = Math.min(radialInside, t - profile.rearT, profile.topT - t);
          const p = field.index(i, j, k);
          const prev = field.data[p] as number;
          if (value > prev) {
            field.data[p] = field.clampValue(value);
            field.dirty[p] = 1;
          }
        }
      }
    }
  }
}

function radiusAt(profile: HoleProfile, t: number): number {
  if (!profile.hasCountersink) {
    if (t <= profile.mouthT) return profile.shaftRadius;
    const relieved = profile.shaftRadius + (profile.blendRadius - profile.countersinkRadius);
    if (t >= 0) return relieved;
    const mix = (t - profile.mouthT) / -profile.mouthT;
    return profile.shaftRadius + (relieved - profile.shaftRadius) * mix;
  }
  if (t <= profile.coneBaseT) return profile.shaftRadius;
  if (t <= profile.mouthT) {
    const mix = (t - profile.coneBaseT) / (profile.mouthT - profile.coneBaseT);
    return profile.shaftRadius + (profile.countersinkRadius - profile.shaftRadius) * mix;
  }
  if (t >= 0) return profile.blendRadius;
  const mix = (t - profile.mouthT) / -profile.mouthT;
  return profile.countersinkRadius + (profile.blendRadius - profile.countersinkRadius) * mix;
}

function pointAt(profile: HoleProfile, t: number): [number, number, number] {
  return [
    profile.surfacePoint[0] + profile.axis[0] * t,
    profile.surfacePoint[1] + profile.axis[1] * t,
    profile.surfacePoint[2] + profile.axis[2] * t,
  ];
}
